import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackEvent } from "../../lib/analytics";
import { MEGA_MENU_GROUPS } from "../../data/productCatalog";
import { useMenuFromDB } from "../../lib/useMenuFromDB";

// All groups + items now live in src/data/productCatalog.js — single source
// of truth shared with /marketplace and the Footer products list. The
// component now optionally pulls a DB-backed override from /api/cms/menus
// (Phase 3 CMS), falling back to the static catalog when no row exists.
const STATIC_SERVICE_GROUPS = MEGA_MENU_GROUPS;

/**
 * <MegaMenu /> — animated hamburger trigger + responsive panel.
 *  Desktop (md+):  click → fullscreen-overlay panel anchored under the nav with a
 *                  multi-column grid of service cards (mega menu).
 *  Mobile (<md):   click → right-side drawer slides in with the same content as
 *                  a vertical scrollable list.
 *
 * Closes on: ESC, route change, backdrop click, or any link click inside.
 */
export default function MegaMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const SERVICE_GROUPS = useMenuFromDB("mega_menu", STATIC_SERVICE_GROUPS);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setOpen((v) => {
      const nv = !v;
      if (nv) trackEvent("services_menu_open", { metadata: { from: location.pathname } });
      return nv;
    });
  }, [location.pathname]);

  // Close on route change.
  useEffect(() => { close(); }, [location.pathname, close]);

  // Close on ESC + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={open ? "Close services menu" : "Open services menu"}
        data-testid="services-menu-trigger"
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 hover:border-gold/60 hover:bg-gold/5 text-white/80 hover:text-gold transition-colors"
      >
        {/* Animated hamburger — three bars morph into an X. */}
        <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
        <span className={`mm-bar mm-bar-1 ${open ? "mm-open" : ""}`} />
        <span className={`mm-bar mm-bar-2 ${open ? "mm-open" : ""}`} />
        <span className={`mm-bar mm-bar-3 ${open ? "mm-open" : ""}`} />
      </button>

      {/* Backdrop */}
      <div
        onClick={close}
        aria-hidden="true"
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      {/* Desktop mega menu panel — md+ only. Anchored from the top under the nav. */}
      <div
        data-testid="services-mega-menu-desktop"
        className={`hidden md:block fixed left-0 right-0 top-16 z-[70] origin-top transform transition-all duration-300 ease-out ${open ? "opacity-100 scale-y-100 pointer-events-auto" : "opacity-0 scale-y-95 pointer-events-none"}`}
      >
        <div className="mx-auto max-w-[1320px] px-6 lg:px-12 pt-6 pb-10">
          <div
            className="rounded-2xl border border-gold/25 bg-[#08080d] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.85),0_0_40px_-10px_rgba(212,175,55,0.25)] p-8 lg:p-10 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-end justify-between mb-7">
              <div>
                <span className="text-[11px] uppercase tracking-[0.28em] text-gold/85">Services & Products</span>
                <h2 className="mt-2 font-display text-3xl tracking-tight">
                  What can we <span className="bk-gold-text italic">build</span> for you?
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/blog"
                  data-testid="services-menu-blog-desktop"
                  onClick={close}
                  className="text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2.5 rounded-full border border-white/15 text-white/80 hover:border-gold/40 hover:text-gold transition-colors"
                >
                  Blog
                </Link>
                <Link
                  to="/marketplace"
                  data-testid="services-menu-marketplace-desktop"
                  onClick={close}
                  className="text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2.5 rounded-full border border-gold/35 text-gold hover:bg-gold/10 transition-colors"
                >
                  Marketplace
                </Link>
                <Link to="/book-a-call" data-testid="services-menu-cta-desktop" className="text-xs uppercase tracking-[0.22em] font-bold px-5 py-2.5 rounded-full bk-shimmer text-black hover:scale-[1.02] transition-transform">
                  Book a Call
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
              {SERVICE_GROUPS.map((group) => (
                <div key={group.label} className="space-y-4">
                  <div className="text-[10px] uppercase tracking-[0.26em] text-white/45 pb-2 border-b border-white/10">
                    {group.label}
                  </div>
                  <ul className="space-y-3">
                    {group.items.map((it) => (
                      <li key={it.to}>
                        <Link
                          to={it.to}
                          data-testid={`services-menu-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={close}
                          className="group flex items-start gap-3 rounded-lg p-2 -mx-2 hover:bg-gold/5 transition-colors"
                        >
                          <span className="block w-16 h-12 flex-shrink-0 rounded-md overflow-hidden border border-white/10 group-hover:border-gold/50 transition-colors bg-black/40">
                            <img
                              src={it.img}
                              alt={it.label}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-2">
                              <span className="block font-display text-[15px] text-white leading-tight group-hover:text-gold transition-colors">
                                {it.label}
                              </span>
                              {it.badge && (
                                <span
                                  className="text-[9px] uppercase tracking-[0.18em] font-bold text-black px-1.5 py-0.5 rounded-full leading-none"
                                  style={{ background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)" }}
                                >
                                  {it.badge}
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-white/55 mt-0.5 leading-snug">{it.tag}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile drawer — <md only. Slides in from the right. */}
      <aside
        data-testid="services-mega-menu-mobile"
        className={`md:hidden fixed top-0 right-0 z-[70] h-[100dvh] w-[88vw] max-w-sm bg-gradient-to-b from-[#08080d] to-[#04060c] border-l border-gold/20 shadow-2xl transform transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
            <div>
              <span className="text-[10px] uppercase tracking-[0.26em] text-gold/85">Services</span>
              <div className="font-display text-lg leading-tight mt-0.5">Bow Tie Kreative</div>
            </div>
            <button
              type="button"
              onClick={close}
              data-testid="services-menu-close-mobile"
              aria-label="Close menu"
              className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-gold hover:border-gold/50 transition-colors flex items-center justify-center"
            >
              <span className="block text-xl leading-none">×</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">
            {SERVICE_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="text-[10px] uppercase tracking-[0.26em] text-white/45 pb-2 mb-2 border-b border-white/10">
                  {group.label}
                </div>
                <ul className="space-y-2">
                  {group.items.map((it) => (
                    <li key={it.to}>
                      <Link
                        to={it.to}
                        data-testid={`services-menu-mobile-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={close}
                        className="flex items-center gap-3 rounded-lg p-2 -mx-2 active:bg-gold/10 hover:bg-gold/5 transition-colors"
                      >
                        <span className="block w-14 h-12 flex-shrink-0 rounded-md overflow-hidden border border-white/10 bg-black/40">
                          <img
                            src={it.img}
                            alt={it.label}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="block font-display text-[15px] text-white leading-tight">
                              {it.label}
                            </span>
                            {it.badge && (
                              <span
                                className="text-[9px] uppercase tracking-[0.18em] font-bold text-black px-1.5 py-0.5 rounded-full leading-none"
                                style={{ background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)" }}
                              >
                                {it.badge}
                              </span>
                            )}
                          </span>
                          <span className="block text-[11px] text-white/55 mt-0.5 leading-snug">{it.tag}</span>
                        </span>
                        <span className="text-gold/60 text-sm">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="px-5 py-5 border-t border-white/8 space-y-2">
            <Link
              to="/blog"
              onClick={close}
              data-testid="services-menu-blog-mobile"
              className="w-full inline-flex items-center justify-center px-5 py-3 rounded-full border border-white/15 text-white/85 text-xs uppercase tracking-[0.22em] font-bold hover:border-gold/40 hover:text-gold transition-colors"
            >
              Read the Blog
            </Link>
            <Link
              to="/book-a-call"
              onClick={close}
              data-testid="services-menu-cta-mobile"
              className="w-full inline-flex items-center justify-center px-5 py-3 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold"
            >
              Book a Call
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}
